import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';
import './AllModal.css';

const ImageUploadModal = ({ isOpen, onClose, onImagesUploaded, existingImages = [], proposalId, hotelId }) => {
  const [images, setImages] = useState(existingImages);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  }, []);

  const handleFiles = (files) => {
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length === 0) {
      alert('Please select only image files.');
      return;
    }

    if (imageFiles.length > 10) {
      alert('You can upload a maximum of 10 images at once.');
      return;
    }

    // Convert files to preview objects
    const newImages = imageFiles.map(file => ({
      id: Date.now() + Math.random(),
      file: file,
      preview: URL.createObjectURL(file),
      isNew: true,
      name: file.name,
      size: file.size
    }));

    setImages(prev => [...prev, ...newImages]);
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const removeImage = (imageId) => {
    setImages(prev => {
      const imageToRemove = prev.find(img => img.id === imageId);
      if (imageToRemove && imageToRemove.preview) {
        URL.revokeObjectURL(imageToRemove.preview);
      }
      return prev.filter(img => img.id !== imageId);
    });
  };

  const handleUpload = async () => {
    if (images.length === 0) {
      alert('Please select at least one image to upload.');
      return;
    }

    setUploading(true);
    
    try {
      // If no hotelId (creating new hotel), just return the images for local storage
      if (!hotelId) {
        const uploadedImages = images.map(image => ({
          id: image.id,
          url: image.preview,
          name: image.file?.name || `image-${image.id}`,
          size: image.file?.size || 0
        }));

        onImagesUploaded(uploadedImages);
        onClose();
        return;
      }

      const formData = new FormData();
      
      // Add new images to form data
      images.forEach((image, index) => {
        if (image.isNew && image.file) {
          formData.append(`images`, image.file);
        }
      });

      // Upload to backend
      const response = await axios.post(
        `${API_BASE_URL}/api/proposals/${proposalId}/hotels/${hotelId}/images`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      // Convert uploaded images to the format expected by the parent component
      const uploadedImages = response.data.data.images.map(image => ({
        id: image.id,
        url: `${API_BASE_URL}${image.url}`,
        name: image.name,
        size: image.size
      }));

      onImagesUploaded(uploadedImages);
      onClose();
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Error uploading images. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    // Clean up object URLs
    images.forEach(image => {
      if (image.preview && image.isNew) {
        URL.revokeObjectURL(image.preview);
      }
    });
    setImages(existingImages);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="image-upload-modal-overlay" onClick={handleClose}>
      <div className="image-upload-modal" onClick={(e) => e.stopPropagation()}>
        <div className="image-upload-modal-header">
          <h3>Upload Hotel Images</h3>
          <button className="close-button" onClick={handleClose}>×</button>
        </div>

        <div className="image-upload-modal-content">
          {/* Drag and Drop Area */}
          <div
            className={`drag-drop-area ${dragActive ? 'drag-active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="drag-drop-content">
              <div className="upload-icon">📁</div>
              <p>Drag and drop images here, or click to select files</p>
              <p className="upload-hint">Supports JPG, PNG, GIF (max 10 images)</p>
            </div>
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileInput}
            style={{ display: 'none' }}
          />

          {/* Image Preview Grid */}
          {images.length > 0 && (
            <div className="image-preview-grid">
              <h4>Selected Images ({images.length})</h4>
              <div className="preview-grid">
                {images.map((image) => (
                  <div key={image.id} className="image-preview-item">
                    <img src={image.preview} alt="Preview" />
                    <button
                      className="remove-image-button"
                      onClick={() => removeImage(image.id)}
                      title="Remove image"
                    >
                      ×
                    </button>
                    <div className="image-info">
                      <span className="image-name">
                        {image.file?.name || `image-${image.id}`}
                      </span>
                      <span className="image-size">
                        {image.file?.size ? `${(image.file.size / 1024 / 1024).toFixed(1)}MB` : ''}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="image-upload-modal-actions">
          <button onClick={handleClose} disabled={uploading}>
            Cancel
          </button>
          <button 
            onClick={handleUpload} 
            disabled={images.length === 0 || uploading}
            className="upload-button"
          >
            {uploading ? 'Uploading...' : `Upload ${images.length} Image${images.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageUploadModal;
